import { Link } from "react-router-dom";
import { MapPin, Users } from "lucide-react";
import { StageBadge, StageAction, stageOf } from "./ProjectStage";
import { EvidenceBadges } from "./EvidenceBadges";

export default function ProjectCard({ project, index = 0 }) {
  const stage = stageOf(project);
  const target = project.target_amount_numeric || 0;
  const raised = project.raised_amount || 0;
  const pct = target ? Math.min(100, Math.round((raised / target) * 100)) : Math.max(0, Math.min(100, project.progress_percent || 0));
  const showMoney = stage !== "under_verification";
  return (
    <article className="group bg-white border border-black/5 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col" data-testid={`project-card-${project.id}`} style={{ animationDelay: `${index * 80}ms` }}>
      <div className="relative h-52 overflow-hidden">
        <img src={project.image_url} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#155e3f]/40 to-transparent" />
        <span className="absolute top-3 left-3 bg-[#155e3f]/85 text-[#d6aa43] text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded">{project.category}</span>
        <StageBadge project={project} className="absolute top-3 right-3" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        {project.serial && <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#d6aa43] font-bold mb-1">{project.serial}</div>}
        <h3 className="font-serif text-xl text-[#0a0d12] font-bold mb-2">{project.title}</h3>
        <p className="text-sm text-[#0a0d12]/70 leading-relaxed mb-3 flex-1">{project.short_description}</p>
        <EvidenceBadges project={project} className="mb-3" />
        <div className="flex items-center gap-4 text-xs text-[#0a0d12]/60 mb-4">
          {project.location && <span className="flex items-center gap-1"><MapPin size={12} /> {project.location}</span>}
          {project.beneficiaries_estimate > 0 && <span className="flex items-center gap-1"><Users size={12} /> {project.beneficiaries_estimate.toLocaleString()} people</span>}
        </div>

        {showMoney ? (
          <>
            <div className="mb-2 flex items-center justify-between text-xs gap-2">
              <span className="text-[#0d1724] font-semibold">{target ? `Budget ₦${target.toLocaleString()}` : project.target_amount}</span>
              <span className="text-[#0d1724] font-bold text-sm" data-testid={`project-progress-${project.id}`}>{pct}% funded</span>
            </div>
            <div className="h-3 bg-[#f1eadb] rounded-full overflow-hidden mb-2 ring-1 ring-black/5" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${pct}% of budget funded`}>
              <div className="h-full rounded-full bg-gradient-to-r from-[#d6aa43] to-[#f0ce77]" style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
            <p className="text-[10px] text-[#0a0d12]/55 mb-5">{stage === "verified_fundraising" ? "Budget approved. Every gift is restricted to this project; spend is published on the project trace." : stage === "funded_implementation" ? "Fully funded. Implementation progress and spend are published on the project trace." : "Completed. Follow-up monitoring at 3, 6 and 12 months is published on the project trace."}</p>
          </>
        ) : (
          <p className="text-[11px] text-[#8a6a1f] bg-[#f8f4e8] border border-[#d6aa43]/30 rounded-md px-3 py-2 mb-5" data-testid={`project-unconfirmed-${project.id}`}>Need reported — budget not yet approved. No project-specific donations are taken until verification is complete.</p>
        )}

        <div className="flex items-center gap-2 flex-wrap self-start">
          <StageAction project={project} className="!py-2.5 !px-4 text-sm" />
          {project.serial && stage !== "completed_monitoring" && stage !== "funded_implementation" && (
            <Link to={`/projects/${encodeURIComponent(project.serial)}/trace`} className="text-xs font-semibold text-[#0d1724]/70 hover:text-[#d6aa43] underline underline-offset-4" data-testid={`project-trace-${project.id}`}>View trace</Link>
          )}
        </div>
      </div>
    </article>
  );
}
